import { useTranslation } from "react-i18next"

import type { MessageVisibilityMode } from "@/api/models"
import type { Participant, ParticipantRole } from "@/services/project-service"

import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"

import { ParticipantListItem } from "./participant-list-item"

type ParticipantListProps = {
  participants: Participant[]
  ownerUserId?: string
  currentUserOid?: string
  canManage: boolean
  isCurrentUserOwner?: boolean
  isLoading?: boolean
  entityType?: "project" | "document" | "chat"
  emptyParticipantsText?: string
  allowedRoles?: ParticipantRole[] // Optional: limit which roles can be selected
  showAccessMode?: boolean // Show message access mode (for chat participants)
  latestMessageId?: string // Latest message ID for chat sessions (used when changing access mode)
  selectedMessageId?: string // Selected message ID (used when switching to specific message)
  onRoleChange?: (participantOid: string, role: ParticipantRole) => void
  onAccessModeChange?: (
    participantOid: string,
    mode: MessageVisibilityMode,
    messageId?: string
  ) => void
  onRemove?: (participantOid: string) => void
  onLeave?: (participantOid: string) => void
  onTransferOwnership?: (newOwnerOid: string) => void
}

export function ParticipantList({
  participants,
  ownerUserId,
  currentUserOid,
  canManage,
  isCurrentUserOwner = false,
  isLoading = false,
  entityType = "project",
  emptyParticipantsText,
  allowedRoles,
  showAccessMode = false,
  latestMessageId,
  selectedMessageId,
  onRoleChange,
  onAccessModeChange,
  onRemove,
  onLeave,
  onTransferOwnership,
}: ParticipantListProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="size-5" />
      </div>
    )
  }

  if (participants.length === 0) {
    return (
      <div className="rounded-md border border-dashed px-4 py-8 text-center">
        <p className="text-muted-foreground text-sm">
          {emptyParticipantsText ??
            t("sharing.no_participants", {
              defaultValue: "No participants yet. Add users or groups to share this project.",
            })}
        </p>
      </div>
    )
  }

  // Sort participants: owner first, then by displayName
  const sortedParticipants = [...participants].sort((a, b) => {
    if (a.oid === ownerUserId) return -1
    if (b.oid === ownerUserId) return 1
    return a.displayName.localeCompare(b.displayName)
  })

  return (
    <ScrollArea className="h-[min(300px,_50dvh)]">
      <div className="space-y-1">
        {sortedParticipants.map((participant) => (
          <ParticipantListItem
            key={participant.oid}
            allowedRoles={allowedRoles}
            canManage={canManage}
            entityType={entityType}
            isCurrentUser={participant.oid === currentUserOid}
            isCurrentUserOwner={isCurrentUserOwner}
            isOwner={participant.oid === ownerUserId}
            latestMessageId={latestMessageId}
            participant={participant}
            selectedMessageId={selectedMessageId}
            showAccessMode={showAccessMode}
            onLeave={onLeave ? () => onLeave(participant.oid) : undefined}
            onRemove={() => onRemove?.(participant.oid)}
            onRoleChange={(role) => onRoleChange?.(participant.oid, role)}
            onTransferOwnership={() => onTransferOwnership?.(participant.oid)}
            onAccessModeChange={(mode, messageId) =>
              onAccessModeChange?.(participant.oid, mode, messageId)
            }
          />
        ))}
      </div>
    </ScrollArea>
  )
}
